"use client"

import { useAutoleadsLocale } from "../../lib/locale-context"
import { useTr } from "../../lib/use-tr"
import { Info, Loader2, Search } from "lucide-react"
import { useMemo, useState } from "react"

import {
  useSaveTeamExportSettings,
  useTeamExportSettings,
} from "@/lib/apps/autoleads/hooks/use-team-mailing-profile"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"

import { columnDescription } from "./column-descriptions"
import { EXPORT_SETTINGS_T } from "./translations"
import type {
  ExportColumn,
  ExportColumnGroup,
  ExportColumns,
  ExportContactLayout,
} from "@/nango/types/apps.autoleads/autoleads/ExportColumns"

// Same cap as the backend's EXPORT_COLUMN_LABEL_MAX_LENGTH.
const LABEL_MAX_LENGTH = 120

const DEFAULT_LAYOUT: ExportContactLayout = "per_contact"

const GROUPS: {
  group: ExportColumnGroup
  titleKey: keyof typeof EXPORT_SETTINGS_T & string
}[] = [
  { group: "sheet", titleKey: "groupSheet" },
  { group: "details", titleKey: "groupDetails" },
  { group: "data", titleKey: "groupData" },
]

const LAYOUTS: {
  layout: ExportContactLayout
  labelKey: keyof typeof EXPORT_SETTINGS_T & string
  hintKey: keyof typeof EXPORT_SETTINGS_T & string
}[] = [
  {
    layout: "per_contact",
    labelKey: "layoutPerContact",
    hintKey: "layoutPerContactHint",
  },
  {
    layout: "per_company",
    labelKey: "layoutPerCompany",
    hintKey: "layoutPerCompanyHint",
  },
]

function defaultIncluded(columns: ExportColumn[]): Set<string> {
  return new Set(
    columns
      .filter((column) => !column.default_hidden)
      .map((column) => column.key),
  )
}

// The team's export setup: which columns its files carry, under what header,
// and one row per contact or per company. One setup shapes every file the
// team gets (csv, xlsx and json downloads, emailed files, weekly delivery),
// so it is opened from the sync panel and from every download menu alike.
export function ExportSettingsDialog({
  teamId,
  open,
  onOpenChange,
}: {
  // Undefined while the caller still resolves it: the dialog waits.
  teamId: number | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const tr = useTr(EXPORT_SETTINGS_T)
  const query = useTeamExportSettings(teamId, open)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-4 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tr("title")}</DialogTitle>
          <DialogDescription>{tr("hint")}</DialogDescription>
        </DialogHeader>
        {query.data && teamId !== undefined ? (
          // Keyed on the saved setup: a fresh opening starts from what is
          // stored, never from an abandoned draft.
          <SettingsEditor
            key={query.dataUpdatedAt}
            teamId={teamId}
            data={query.data}
            onDone={() => onOpenChange(false)}
          />
        ) : query.isError ? (
          <p className="text-destructive text-sm">{tr("failedToLoad")}</p>
        ) : (
          <div className="flex justify-center py-10">
            <Loader2 className="text-muted-foreground size-5 animate-spin" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SettingsEditor({
  teamId,
  data,
  onDone,
}: {
  teamId: number
  data: ExportColumns
  onDone: () => void
}) {
  const tr = useTr(EXPORT_SETTINGS_T)
  const [included, setIncluded] = useState(() => {
    const hidden = new Set(data.hidden)
    const shown = new Set(data.shown)
    return new Set(
      data.columns
        .filter(
          (column) =>
            !hidden.has(column.key) &&
            (shown.has(column.key) || !column.default_hidden),
        )
        .map((column) => column.key),
    )
  })
  const [labels, setLabels] = useState<Record<string, string>>(data.labels)
  const [layout, setLayout] = useState<ExportContactLayout>(data.contact_layout)
  const [search, setSearch] = useState("")
  const save = useSaveTeamExportSettings(teamId, {
    successMessage: tr("saved"),
    onSuccess: onDone,
  })

  const needle = search.trim().toLowerCase()
  const matching = useMemo(
    () =>
      data.columns.filter(
        (column) =>
          !needle ||
          column.key.toLowerCase().includes(needle) ||
          (labels[column.key] ?? "").toLowerCase().includes(needle),
      ),
    [data.columns, labels, needle],
  )
  // Two kept columns under one header would leave a sheet nobody can read
  // back: flagged on the row before the server refuses it.
  const duplicates = useMemo(() => {
    const headers = data.columns
      .filter((column) => included.has(column.key))
      .map((column) => labels[column.key]?.trim() || column.key)
    return new Set(
      headers.filter((header, index) => headers.indexOf(header) !== index),
    )
  }, [data.columns, included, labels])

  function toggle(keys: string[], include: boolean) {
    setIncluded((current) => {
      const next = new Set(current)
      for (const key of keys) {
        if (include) next.add(key)
        else next.delete(key)
      }
      return next
    })
  }

  function submit() {
    // Stored as departures from the defaults, so a column added later lands
    // in the files with its own default.
    save.mutate({
      hidden: data.columns
        .filter((column) => !column.default_hidden && !included.has(column.key))
        .map((column) => column.key),
      shown: data.columns
        .filter((column) => column.default_hidden && included.has(column.key))
        .map((column) => column.key),
      labels,
      contact_layout: layout,
    })
  }

  const layoutHint = LAYOUTS.find((entry) => entry.layout === layout)?.hintKey

  return (
    <>
      <Field>
        <FieldLabel>{tr("layoutLabel")}</FieldLabel>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={layout}
          onValueChange={(value) => {
            if (value) setLayout(value as ExportContactLayout)
          }}
          aria-label={tr("layoutLabel")}
        >
          {LAYOUTS.map(({ layout: value, labelKey }) => (
            <ToggleGroupItem
              key={value}
              value={value}
              className="flex-none px-3"
            >
              {tr(labelKey)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {layoutHint ? (
          <FieldDescription>{tr(layoutHint)}</FieldDescription>
        ) : null}
      </Field>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <InputGroup className="sm:max-w-xs">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tr("search")}
            aria-label={tr("search")}
          />
        </InputGroup>
        <span className="text-muted-foreground text-xs tabular-nums">
          {tr("count")
            .replace("{kept}", String(included.size))
            .replace("{total}", String(data.columns.length))}
        </span>
      </div>

      <div className="-mx-6 min-h-0 flex-1 space-y-6 overflow-y-auto px-6">
        {GROUPS.map(({ group, titleKey }) => {
          const columns = matching.filter((column) => column.group === group)
          if (columns.length === 0) return null
          const keys = columns.map((column) => column.key)
          const visible = keys.filter((key) => included.has(key)).length
          return (
            <section key={group} className="space-y-1">
              <label className="flex items-center gap-2 py-1">
                <Checkbox
                  checked={
                    visible === keys.length
                      ? true
                      : visible > 0
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(next) => toggle(keys, next === true)}
                />
                <span className="text-foreground text-sm font-semibold">
                  {tr(titleKey)}
                </span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {visible}/{keys.length}
                </span>
              </label>
              <ul className="divide-border divide-y">
                {columns.map((column) => (
                  <ColumnRow
                    key={column.key}
                    column={column}
                    included={included.has(column.key)}
                    label={labels[column.key] ?? ""}
                    duplicate={
                      included.has(column.key) &&
                      duplicates.has(labels[column.key]?.trim() || column.key)
                    }
                    onToggle={(include) => toggle([column.key], include)}
                    onRename={(label) =>
                      setLabels((current) => ({
                        ...current,
                        [column.key]: label,
                      }))
                    }
                  />
                ))}
              </ul>
            </section>
          )
        })}
        {matching.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            {tr("noMatch")}
          </p>
        ) : null}
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        <Button
          type="button"
          variant="ghost"
          disabled={save.isPending}
          onClick={() => {
            setIncluded(defaultIncluded(data.columns))
            setLabels({})
            setLayout(DEFAULT_LAYOUT)
          }}
        >
          {tr("reset")}
        </Button>
        <Button
          type="button"
          variant="cta"
          disabled={
            save.isPending || included.size === 0 || duplicates.size > 0
          }
          onClick={submit}
        >
          {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          {tr("save")}
        </Button>
      </DialogFooter>
    </>
  )
}

function ColumnRow({
  column,
  included,
  label,
  duplicate,
  onToggle,
  onRename,
}: {
  column: ExportColumn
  included: boolean
  label: string
  duplicate: boolean
  onToggle: (include: boolean) => void
  onRename: (label: string) => void
}) {
  const tr = useTr(EXPORT_SETTINGS_T)
  const locale = useAutoleadsLocale()
  const description = columnDescription(column.key, locale)
  const checkboxId = `export-column-${column.key}`
  return (
    <li className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <label htmlFor={checkboxId} className="flex min-w-0 items-center gap-2">
          <Checkbox
            id={checkboxId}
            checked={included}
            onCheckedChange={(next) => onToggle(next === true)}
          />
          <span
            className={
              included
                ? "text-foreground truncate font-mono text-xs"
                : "text-muted-foreground truncate font-mono text-xs line-through"
            }
          >
            {column.key}
          </span>
        </label>
        {/* A popover, not a tooltip: the hint has to open on touch too. */}
        {description ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                aria-label={tr("columnInfo").replace("{column}", column.key)}
              >
                <Info className="size-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 text-xs">
              {description}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
      <div className="flex flex-col gap-1 sm:w-56">
        <Input
          value={label}
          onChange={(event) => onRename(event.target.value)}
          placeholder={tr("renamePlaceholder")}
          maxLength={LABEL_MAX_LENGTH}
          disabled={!included}
          aria-label={tr("rename").replace("{column}", column.key)}
          aria-invalid={duplicate}
          aria-describedby={duplicate ? `${checkboxId}-error` : undefined}
          className="h-8 text-sm"
        />
        {duplicate ? (
          <p id={`${checkboxId}-error`} className="text-destructive text-xs">
            {tr("duplicate")}
          </p>
        ) : null}
      </div>
    </li>
  )
}
